<template>
  <Card class="p-6 hover:shadow-lg transition-shadow">
    <div class="flex flex-col space-y-4">
      <!-- Header with name and provider -->
      <div class="flex items-start justify-between">
        <div class="flex-1 min-w-0">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
            {{ model.name }}
          </h3>
          <div class="mt-1 flex items-center space-x-2">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                providerBadgeColor,
              ]"
            >
              {{ providerDisplayName }}
            </span>
            <code
              class="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded"
            >
              {{ model.slug }}
            </code>
          </div>
        </div>
        <span
          :class="[
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
            statusBadgeColor,
          ]"
        >
          {{ statusText }}
        </span>
      </div>

      <!-- Pricing Information -->
      <div class="grid grid-cols-2 gap-3">
        <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Input Cost</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            ${{ (model.input_cost_1m ?? 0).toFixed(2)
            }}<span class="text-xs font-normal text-gray-500 dark:text-gray-400">/1M tokens</span>
          </p>
        </div>
        <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Output Cost</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            ${{ (model.output_cost_1m ?? 0).toFixed(2)
            }}<span class="text-xs font-normal text-gray-500 dark:text-gray-400">/1M tokens</span>
          </p>
        </div>
      </div>

      <!-- Context Window -->
      <div class="flex items-center justify-between text-sm">
        <span class="text-gray-500 dark:text-gray-400">Context Window</span>
        <span class="font-medium text-gray-900 dark:text-white">
          {{ formatContextWindow(model.context_window) }}
        </span>
      </div>

      <!-- Modalities -->
      <div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">Input</p>
        <div class="flex flex-wrap gap-1">
          <span
            v-for="mod in model.input_modalities"
            :key="'in-' + mod"
            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
          >
            {{ formatModality(mod) }}
          </span>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2 mt-2">Output</p>
        <div class="flex flex-wrap gap-1">
          <span
            v-for="mod in model.output_modalities"
            :key="'out-' + mod"
            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
          >
            {{ formatModality(mod) }}
          </span>
        </div>
      </div>

      <!-- Actions -->
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div v-if="!isAddedToOrg" class="space-y-2">
          <!-- Add to Organization Section -->
          <div v-if="showNameInput" class="space-y-2">
            <Input
              v-model="customName"
              placeholder="Custom name (optional, defaults to slug)"
              class="w-full"
              :disabled="isSubmitting"
            />
            <div class="flex space-x-2">
              <Button
                variant="primary"
                size="sm"
                class="flex-1"
                :disabled="isSubmitting"
                @click="handleAddToOrg"
              >
                <span v-if="isSubmitting">Adding...</span>
                <span v-else>Confirm Add</span>
              </Button>
              <Button variant="ghost" size="sm" :disabled="isSubmitting" @click="cancelAdd">
                Cancel
              </Button>
            </div>
          </div>
          <Button v-else variant="primary" size="sm" class="w-full" @click="showNameInput = true">
            Add to Organization
          </Button>
        </div>

        <div v-else class="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            class="flex-1"
            :disabled="isSubmitting"
            @click="handleToggleStatus"
          >
            <span v-if="isSubmitting">Processing...</span>
            <span v-else>{{ model.is_enabled_for_org ? 'Disable' : 'Enable' }}</span>
          </Button>
          <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- icon-only delete action in a menu, not a standalone Button -->
          <Button
            variant="ghost"
            size="sm"
            class="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            :disabled="isSubmitting"
            @click="handleRemove"
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { Card, Button, Input } from '@asabdev/asab-ui';
  import type { BaseModelResponse } from '../types';

  interface Props {
    model: BaseModelResponse;
  }

  interface Emits {
    (e: 'add', data: { slug: string; name?: string }): void;
    (e: 'enable', slug: string): void;
    (e: 'disable', slug: string): void;
    (e: 'remove', slug: string): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  // Local state
  const showNameInput = ref(false);
  const customName = ref('');
  const isSubmitting = ref(false);

  // Computed properties
  const providerDisplayName = computed(() => {
    const providerMap: Record<string, string> = {
      openai: 'OpenAI',
      anthropic: 'Anthropic',
      google: 'Google',
      groq: 'Groq',
    };
    return providerMap[props.model.provider] || props.model.provider;
  });

  const providerBadgeColor = computed(() => {
    const colorMap: Record<string, string> = {
      openai: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      anthropic: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
      google: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      groq: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
    };
    return (
      colorMap[props.model.provider] ||
      'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300'
    );
  });

  const isAddedToOrg = computed(() => {
    return props.model.is_added_to_org ?? props.model.is_enabled_for_org ?? false;
  });

  const statusBadgeColor = computed(() => {
    if (props.model.is_enabled_for_org) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
    }
    if (isAddedToOrg.value) {
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
    }
    return 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300';
  });

  const statusText = computed(() => {
    if (props.model.is_enabled_for_org) return 'Enabled';
    if (isAddedToOrg.value) return 'Disabled';
    return 'Available';
  });

  // Methods
  const formatContextWindow = (tokens: number): string => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M tokens`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(0)}K tokens`;
    }
    return `${tokens} tokens`;
  };

  const formatModality = (mod: string): string => {
    const map: Record<string, string> = {
      text: 'Text',
      image: 'Image',
      audio: 'Audio',
      video: 'Video',
      file: 'File',
      embeddings: 'Embeddings',
    };
    return map[mod] || mod;
  };

  const handleAddToOrg = () => {
    isSubmitting.value = true;
    const name = customName.value.trim() || undefined;
    emit('add', { slug: props.model.slug, name });
    // Reset state after emit
    setTimeout(() => {
      isSubmitting.value = false;
      showNameInput.value = false;
      customName.value = '';
    }, 500);
  };

  const cancelAdd = () => {
    showNameInput.value = false;
    customName.value = '';
  };

  const handleToggleStatus = () => {
    isSubmitting.value = true;
    if (props.model.is_enabled_for_org) {
      emit('disable', props.model.slug);
    } else {
      emit('enable', props.model.slug);
    }
    // Reset submitting state after a delay
    setTimeout(() => {
      isSubmitting.value = false;
    }, 500);
  };

  const handleRemove = () => {
    isSubmitting.value = true;
    emit('remove', props.model.slug);
    // Reset submitting state after a delay
    setTimeout(() => {
      isSubmitting.value = false;
    }, 500);
  };
</script>
